import * as z$1 from "zod/v4";
//#region src/schemas/config/model-catalog.ts
const MODEL_CATALOG_SCHEMA_VERSION = 1;
const boundedIdentifierSchema = z$1.string().trim().min(1).max(256);
const boundedDisplayNameSchema = z$1.string().trim().min(1).max(256);
const nullableBoundedIdentifierSchema = boundedIdentifierSchema.nullable();
const modelCatalogBadgeLabelSchema = z$1.string().trim().min(1).max(32);
const hostedModelInputModalitySchema = z$1.enum(["text", "image"]);
const hostedModelReasoningLevelSchema = z$1.enum([
	"off",
	"minimal",
	"low",
	"medium",
	"high",
	"xhigh"
]);
const modelCatalogHostedRuntimeSchema = z$1.strictObject({
	provider: z$1.literal("openrouter"),
	inputModalities: z$1.array(hostedModelInputModalitySchema).min(1),
	contextWindow: z$1.number().int().positive(),
	maxOutputTokens: z$1.number().int().positive(),
	reasoningLevel: hostedModelReasoningLevelSchema
});
const modelCatalogModelSchema = z$1.strictObject({
	id: boundedIdentifierSchema,
	displayName: boundedDisplayNameSchema,
	badgeLabel: modelCatalogBadgeLabelSchema.optional(),
	hostedRuntime: modelCatalogHostedRuntimeSchema.optional(),
	unavailable: z$1.boolean(),
	supportedReasoningLevels: z$1.array(boundedIdentifierSchema),
	minCliVersion: nullableBoundedIdentifierSchema
});
const modelCatalogHostedRuntimePolicySchema = z$1.strictObject({
	automaticModelFallback: z$1.literal(false),
	imageUnderstandingModelId: boundedIdentifierSchema
});
const modelCatalogAgentControllerSchema = z$1.strictObject({
	defaultModelId: boundedIdentifierSchema,
	creationDefaults: z$1.strictObject({
		modelId: boundedIdentifierSchema,
		reasoningLevel: nullableBoundedIdentifierSchema
	}).optional(),
	hostedRuntimePolicy: modelCatalogHostedRuntimePolicySchema.optional(),
	models: z$1.array(modelCatalogModelSchema).min(1)
}).superRefine((value, context) => {
	const modelIds = /* @__PURE__ */ new Set();
	for (const [index, model] of value.models.entries()) {
		if (modelIds.has(model.id)) context.addIssue({
			code: "custom",
			message: "model ids must be unique per agent controller",
			path: [
				"models",
				index,
				"id"
			]
		});
		modelIds.add(model.id);
		const reasoningLevels = /* @__PURE__ */ new Set();
		for (const [reasoningLevelIndex, reasoningLevel] of model.supportedReasoningLevels.entries()) {
			if (!reasoningLevels.has(reasoningLevel)) {
				reasoningLevels.add(reasoningLevel);
				continue;
			}
			context.addIssue({
				code: "custom",
				message: "reasoning levels must be unique per model",
				path: [
					"models",
					index,
					"supportedReasoningLevels",
					reasoningLevelIndex
				]
			});
		}
	}
	if (!modelIds.has(value.defaultModelId)) context.addIssue({
		code: "custom",
		message: "defaultModelId must reference a listed model",
		path: ["defaultModelId"]
	});
	if (value.models.find((model) => model.id === value.defaultModelId)?.unavailable) context.addIssue({
		code: "custom",
		message: "defaultModelId must reference an available model",
		path: ["defaultModelId"]
	});
}).superRefine((value, context) => {
	if (value.creationDefaults) {
		const selected = value.models.find((model) => model.id === value.creationDefaults?.modelId);
		if (!selected || selected.unavailable) context.addIssue({
			code: "custom",
			message: "creationDefaults must select an available model",
			path: ["creationDefaults", "modelId"]
		});
		else if (value.creationDefaults.reasoningLevel !== null && !selected.supportedReasoningLevels.includes(value.creationDefaults.reasoningLevel)) context.addIssue({
			code: "custom",
			message: "creationDefaults reasoning must be supported by its model",
			path: ["creationDefaults", "reasoningLevel"]
		});
		if (value.hostedRuntimePolicy) context.addIssue({
			code: "custom",
			message: "hosted controllers do not have local profile creation defaults",
			path: ["creationDefaults"]
		});
	}
	if (!value.hostedRuntimePolicy) return;
	for (const [index, model] of value.models.entries()) {
		if (model.hostedRuntime) continue;
		context.addIssue({
			code: "custom",
			message: "hosted controller models must define hostedRuntime",
			path: [
				"models",
				index,
				"hostedRuntime"
			]
		});
	}
	const imageUnderstandingModel = value.models.find((model) => model.id === value.hostedRuntimePolicy?.imageUnderstandingModelId);
	if (!imageUnderstandingModel) {
		context.addIssue({
			code: "custom",
			message: "imageUnderstandingModelId must reference a listed hosted model",
			path: ["hostedRuntimePolicy", "imageUnderstandingModelId"]
		});
		return;
	}
	if (imageUnderstandingModel.unavailable) context.addIssue({
		code: "custom",
		message: "imageUnderstandingModelId must reference an available hosted model",
		path: ["hostedRuntimePolicy", "imageUnderstandingModelId"]
	});
	if (!imageUnderstandingModel.hostedRuntime?.inputModalities.includes("image")) context.addIssue({
		code: "custom",
		message: "imageUnderstandingModelId must support image input",
		path: ["hostedRuntimePolicy", "imageUnderstandingModelId"]
	});
});
const modelCatalogSchema = z$1.strictObject({
	schemaVersion: z$1.literal(1),
	updatedAt: z$1.iso.datetime({ offset: true }),
	agentControllers: z$1.record(boundedIdentifierSchema, modelCatalogAgentControllerSchema)
});
const getModelCatalogResponseSchema = modelCatalogSchema;
z$1.strictObject({
	schemaVersion: z$1.literal(1),
	controller: boundedIdentifierSchema,
	observedAt: z$1.iso.datetime({ offset: true }),
	fingerprint: z$1.string().regex(/^[a-f0-9]{64}$/),
	sources: z$1.array(z$1.strictObject({
		url: z$1.url(),
		kind: z$1.enum([
			"official-release",
			"official-harness-catalog",
			"official-document",
			"discovery-only"
		]),
		data: z$1.json()
	})).min(1),
	changes: z$1.array(z$1.strictObject({
		modelId: boundedIdentifierSchema,
		before: modelCatalogModelSchema.nullable(),
		after: modelCatalogModelSchema
	})),
	manual: z$1.array(z$1.string())
});
//#endregion
export { MODEL_CATALOG_SCHEMA_VERSION, getModelCatalogResponseSchema, hostedModelInputModalitySchema, hostedModelReasoningLevelSchema, modelCatalogAgentControllerSchema, modelCatalogBadgeLabelSchema, modelCatalogHostedRuntimePolicySchema, modelCatalogHostedRuntimeSchema, modelCatalogModelSchema, modelCatalogSchema };

//# sourceMappingURL=model-catalog.js.map